import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AlertBanner, Badge, Button, DashboardPanel } from '@rentflowdev/ui'
import { CheckIcon } from 'lucide-react'

export const Route = createFileRoute('/_app/settings/billing')({
	component: SettingsBillingPage,
})

interface Plan {
	id:    'basic' | 'extended' | 'professional'
	popular?: boolean
	current?: boolean
}

const PLANS: Plan[] = [
	{ id: 'basic',         current: true },
	{ id: 'extended',      popular: true },
	{ id: 'professional' },
]

function SettingsBillingPage() {
	const { t } = useTranslation()

	return (
		<div className="flex flex-col gap-4 px-4 py-4">
			<AlertBanner
				title={t('settings.billing.limitReached')}
				description={t('settings.billing.limitDescription', { used: 50, total: 50, tariff: t('settings.billing.plan.basic') })}
				action={<Button size="sm">{t('settings.billing.upgradeBtn')}</Button>}
			/>

			<div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
				<SummaryTile label={t('settings.billing.summary.current')}>
					{t('settings.billing.plan.basic')}
				</SummaryTile>
				<SummaryTile label={t('settings.billing.summary.nextPayment')}>
					02.08.2025
				</SummaryTile>
				<SummaryTile label={t('settings.billing.summary.objects')}>
					<span className="text-sm">50 / 50</span>
					<div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-(--color-muted)">
						<div className="h-full w-full bg-(--color-destructive)" />
					</div>
				</SummaryTile>
				<SummaryTile label={t('settings.billing.summary.employees')}>
					<span className="text-sm">3 / 10</span>
					<div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-(--color-muted)">
						<div className="h-full w-[30%] bg-(--color-success)" />
					</div>
				</SummaryTile>
			</div>

			<div className="flex gap-2">
				<Button size="sm">{t('settings.billing.summary.changeTariff')}</Button>
				<Button size="sm" variant="default">{t('settings.billing.summary.payNow')}</Button>
			</div>

			<div className="grid grid-cols-1 gap-3 md:grid-cols-3">
				{PLANS.map((p) => (
					<DashboardPanel
						key={p.id}
						title={t(`settings.billing.plan.${p.id}`)}
						subtitle={
							<span className="text-2xl font-[600] text-(--color-foreground)">
								{t(`settings.billing.plan.${p.id}Price`)}
							</span>
						}
						actions={p.popular ? <Badge variant="info" size="sm">{t('settings.billing.plan.popular')}</Badge> : undefined}
					>
						<ul className="flex flex-col gap-1 text-sm text-(--color-muted-foreground)">
							<li className="flex items-center gap-2"><CheckIcon className="size-4" /> Преимущество 1</li>
							<li className="flex items-center gap-2"><CheckIcon className="size-4" /> Преимущество 2</li>
							<li className="flex items-center gap-2"><CheckIcon className="size-4" /> Преимущество 3</li>
						</ul>
						<Button size="sm" disabled={p.current} variant={p.current ? 'secondary' : 'default'}>
							{p.current ? t('settings.billing.plan.current') : t('settings.billing.plan.connect')}
						</Button>
					</DashboardPanel>
				))}
			</div>

			<p className="text-xs italic text-(--color-muted-foreground)">
				{t('settings.billing.notReady')}
			</p>
		</div>
	)
}

function SummaryTile({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<DashboardPanel headless className="gap-1 p-3">
			<span className="text-xs text-(--color-muted-foreground)">{label}</span>
			<span className="text-sm font-[500]">{children}</span>
		</DashboardPanel>
	)
}
